// US quotation route map.
//
// Phase B redesign: the operator lands on a read-only quote detail
// (`/quotes/:id`) after generation instead of being dropped straight onto the
// accept panel. Marketplace got `/shop`, `/shop/compare`, `/shop/review` so
// plan selection no longer commits a DB row up-front.
//
// ⚠️ Signing USED to have its own CRM URL (`/quotes/:id/sign`). It no longer
// exists — Azure #13266: the CRM does not sign contracts, the customer does.
//
//   /quotes                            list (stage-filter chips)
//   /quotes/quick                      quick-quote (auto-cheapest)
//   /quotes/bulk                       bulk-quote job list
//   /quotes/bulk/:jobUId               one job: summary + per-row results
//   /quotes/new                        4-step wizard (step 4 = Pick plan)
//   /quotes/shop                       service point (resolve the meter)
//   /quotes/shop/plans                 priced plan shelf (multi-select + compare)
//   /quotes/shop/compare               side-by-side comparison
//   /quotes/shop/review                customer + locked plan → generate
//   /quotes/marketplace                back-compat alias → shop
//   /quotes/settings                   per-tenant Expiry & Renewal settings
//   /quotes/:id                        read-only detail (replaces old DRAFT-with-sign-panel)
//   /quotes/:id/sign                   RETIRED (Azure #13266) -> redirects to /quotes/:id
//   /quotes/:id/enrollment             post-accept progress + rescission
//   /quotes/:id/edit                   back-compat redirect → /quotes/:id

import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

const ViewQuotesPage = lazy(() =>
  import('./pages/ViewQuotesPage').then((m) => ({ default: m.ViewQuotesPage })),
);
const QuickQuotePage = lazy(() =>
  import('./pages/QuickQuotePage').then((m) => ({ default: m.QuickQuotePage })),
);
const BulkQuoteJobsPage = lazy(() =>
  import('./pages/BulkQuoteJobsPage').then((m) => ({ default: m.BulkQuoteJobsPage })),
)
const BulkQuoteJobDetailPage = lazy(() =>
  import('./pages/BulkQuoteJobDetailPage').then((m) => ({ default: m.BulkQuoteJobDetailPage })),
)
const QuoteWizardPage = lazy(() =>
  import('./pages/QuoteWizardPage').then((m) => ({ default: m.QuoteWizardPage })),
);
const MarketplacePage = lazy(() =>
  import('./pages/MarketplacePage').then((m) => ({ default: m.MarketplacePage })),
);
const PlansPage = lazy(() =>
  import('./pages/PlansPage').then((m) => ({ default: m.PlansPage })),
);
const ComparePlansPage = lazy(() =>
  import('./pages/ComparePlansPage').then((m) => ({ default: m.ComparePlansPage })),
);
const ReviewAndGeneratePage = lazy(() =>
  import('./pages/ReviewAndGeneratePage').then((m) => ({ default: m.ReviewAndGeneratePage })),
);
const QuoteExpirySettingsPage = lazy(() =>
  import('./pages/QuoteExpirySettingsPage').then((m) => ({
    default: m.QuoteExpirySettingsPage,
  })),
);
const QuoteDetailPage = lazy(() =>
  import('./pages/QuoteDetailPage').then((m) => ({ default: m.QuoteDetailPage })),
);
const EnrollmentStatusPage = lazy(() =>
  import('./pages/EnrollmentStatusPage').then((m) => ({ default: m.EnrollmentStatusPage })),
);
const EditQuoteRedirectShim = lazy(() =>
  import('./pages/EditQuoteRedirectShim').then((m) => ({ default: m.EditQuoteRedirectShim })),
);
// FE-US-11 — Ops enrollment issues page. Role-guard is inline in the
// page itself (no shared RoleGuard wrapper exists yet — when one lands
// this route will be migrated).
const OpsEnrollmentIssuesPage = lazy(() =>
  import('./pages/OpsEnrollmentIssuesPage').then((m) => ({
    default: m.OpsEnrollmentIssuesPage,
  })),
);

export const quotationRoutes: RouteObject[] = [
  { path: '/quotes', element: <ViewQuotesPage /> },
  { path: '/quotes/quick', element: <QuickQuotePage /> },
  { path: '/quotes/bulk', element: <BulkQuoteJobsPage /> },
  { path: '/quotes/bulk/:jobUId', element: <BulkQuoteJobDetailPage /> },
  { path: '/quotes/new', element: <QuoteWizardPage /> },
  // Marketplace + Phase C comparison/review surfaces.
  { path: '/quotes/shop', element: <MarketplacePage /> },
  { path: '/quotes/shop/plans', element: <PlansPage /> },
  { path: '/quotes/shop/compare', element: <ComparePlansPage /> },
  { path: '/quotes/shop/review', element: <ReviewAndGeneratePage /> },
  // Back-compat: any saved bookmark pointing at the old name still resolves.
  { path: '/quotes/marketplace', element: <MarketplacePage /> },
  // Per-tenant quote lifecycle settings. MUST stay above `/quotes/:quoteId` for
  // readability; React Router ranks the static segment higher regardless, the
  // same way /quotes/quick and /quotes/new already coexist with the id route.
  { path: '/quotes/settings', element: <QuoteExpirySettingsPage /> },
  // Phase D quote-scoped routes.
  { path: '/quotes/:quoteId', element: <QuoteDetailPage /> },
  // Azure #13266 — the CRM signing ceremony is GONE. The CRM does not sign
  // contracts; the agent picks a tariff and the customer signs it themselves via
  // an emailed link (see `SendForSignatureButton`). Redirected rather than
  // deleted because this URL is in bookmarks and older emails — see the shim.
  { path: '/quotes/:quoteId/sign', element: <EditQuoteRedirectShim /> },
  { path: '/quotes/:quoteId/enrollment', element: <EnrollmentStatusPage /> },
  // Back-compat redirect: /quotes/:id/edit -> /quotes/:id.
  { path: '/quotes/:quoteId/edit', element: <EditQuoteRedirectShim /> },
  // FE-US-11 — Ops surface for the failure-recovery queue.
  { path: '/ops/enrollment-issues', element: <OpsEnrollmentIssuesPage /> },
];
